import React, { useState, useEffect } from "react";
import { Card, Title, Button, Flex, Text, Divider } from "@tremor/react";
import ProfilesTable from "./table";
import ProfileForm from "./form";
import { Dialog, DialogPanel } from "@tremor/react";
import Nav from "../../components/nav";
import Toast from "../../components/Toast";
import { useLocation } from "react-router-dom";

import * as profilesAPI from "../../services/profiles";
import { fetchDevices } from "../../services/device";
import {
  PlusCircleIcon,
  CubeTransparentIcon,
} from "@heroicons/react/24/outline";

function ProfilesTableSkeleton() {
  return (
    <div className="animate-pulse overflow-x-auto">
      <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex-1 grid grid-cols-3 gap-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-8"></div>
        </div>
      </div>
      {[...Array(3)].map((_, index) => (
        <div key={index} className="flex items-center p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex-1 grid grid-cols-3 gap-4 items-center">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-2/3"></div>
            </div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/2"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-8"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [page] = useState(1);
  const [limit] = useState(10);
  const [, setTotal] = useState(0);
  const [isOpen, setIsOpen] = React.useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "" });
  const [deviceCounts, setDeviceCounts] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blockedDevices, setBlockedDevices] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const location = useLocation();

  const refresh = async () => {
    try {
      await getProfiles();
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onDelete = (id) => {
    const target = profiles.find((p) => p._id === id);
    if (!target) return;
    setDeleteTarget(target);
    setBlockedDevices(null);
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setBlockedDevices(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await profilesAPI.remove(deleteTarget._id);
      await getProfiles();
      setDeleteTarget(null);
      setBlockedDevices(null);
    } catch (error) {
      if (error?.status === 400 && Array.isArray(error.devices)) {
        setBlockedDevices(error.devices);
      } else {
        setDeleteError(error?.error || error?.message || "Failed to delete profile");
      }
    } finally {
      setDeleting(false);
    }
  };

  const getProfiles = async () => {
    setLoading(true);
    try {
      const response = await profilesAPI.list(page, limit);
      setProfiles(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.success) {
      setToast({ show: true, message: location.state.success });
      // Clear the state so toast doesn't reappear on refresh
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Read operation
  useEffect(() => {
    getProfiles();

    const getDeviceCounts = async () => {
      try {
        const devices = await fetchDevices();
        const counts = {};
        devices.forEach(d => {
          if (!d.profile) return;
          // profile may be a populated object or a string ID
          const profileId = typeof d.profile === 'string' ? d.profile : d.profile._id;
          if (profileId) {
            counts[profileId] = (counts[profileId] || 0) + 1;
          }
        });
        setDeviceCounts(counts);
      } catch (err) {
        console.error('Error fetching device counts:', err);
      }
    };
    getDeviceCounts();
  }, []);

  return (
    <div>
      {toast.show && (
        <Toast
          message={toast.message}
          type="success"
          duration={3000}
          onClose={() => setToast({ show: false, message: "" })}
        />
      )}
      <Nav />
      <main className="p-4 md:p-10 mx-auto max-w-7xl">
        <Flex>
          <div>
            <Title>Profiles</Title>
            <Text>Profiles are device definitions for LoRa devices.</Text>
          </div>
          <div>
            <Button variant="primary" size="xs" onClick={() => setIsOpen(true)}>
              New Profile
            </Button>
          </div>
        </Flex>
        <Divider />
        <Card className="mt-6">
          {loading ? (
            <ProfilesTableSkeleton />
          ) : profiles.length > 0 ? (
            <ProfilesTable profiles={profiles} onDelete={onDelete} deviceCounts={deviceCounts} />
          ) : (
            <NoData open={() => setIsOpen(true)} />
          )}
        </Card>
      </main>
      <Dialog open={isOpen} onClose={(val) => setIsOpen(val)} static={true}>
        <DialogPanel>
          <ProfileForm onCancel={refresh} />
        </DialogPanel>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={closeDeleteDialog}
        static={true}
        className="relative z-50"
      >
        <DialogPanel className="max-w-md transform rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl transition-all">
          {blockedDevices ? (
            <>
              <Title>Can't delete this profile</Title>
              <Text className="mt-2">
                <strong>{deleteTarget?.name}</strong> is in use by{" "}
                {blockedDevices.length} device
                {blockedDevices.length === 1 ? "" : "s"}. Reassign or delete the
                device{blockedDevices.length === 1 ? "" : "s"} first, then try
                again.
              </Text>
              <ul className="mt-3 list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                {blockedDevices.map((d) => (
                  <li key={d._id} className="text-sm text-gray-700 dark:text-gray-300">
                    {d.name || d._id}
                  </li>
                ))}
              </ul>
              <Flex justifyContent="end" className="mt-4">
                <Button onClick={closeDeleteDialog}>Close</Button>
              </Flex>
            </>
          ) : (
            <>
              <Title>Delete profile?</Title>
              <Text className="mt-2">
                This will permanently delete{" "}
                <strong>{deleteTarget?.name}</strong>. This cannot be undone.
              </Text>
              {deleteError && (
                <div className="bg-red-100 dark:bg-red-950/40 px-2 py-1 mt-3">
                  <Text className="text-red-600 dark:text-red-300">{deleteError}</Text>
                </div>
              )}
              <Flex justifyContent="end" className="mt-4 space-x-2">
                <Button
                  variant="secondary"
                  onClick={closeDeleteDialog}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button color="red" onClick={confirmDelete} loading={deleting}>
                  Delete
                </Button>
              </Flex>
            </>
          )}
        </DialogPanel>
      </Dialog>
    </div>
  );
}

function NoData({ open }) {
  return (
    <>
      <div className="mt-4 flex h-52 items-center justify-center rounded-tremor-small border border-dashed border-tremor-border p-4 dark:border-dark-tremor-border">
        <div className="text-center">
          <CubeTransparentIcon
            className="mx-auto h-7 w-7 text-tremor-content-subtle dark:text-dark-tremor-content-subtle"
            aria-hidden={true}
          />

          <p className="mt-2 text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            Empty
          </p>
          <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            Create a new profile and define data format and decoder for your
            devices.
          </p>
          <p></p>
          <Button
            icon={PlusCircleIcon}
            onClick={open}
            type="button"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-tremor-small bg-tremor-brand px-3 py-2 text-tremor-default font-medium text-tremor-brand-inverted shadow-tremor-input hover:bg-tremor-brand-emphasis"
          >
            New Profile
          </Button>
        </div>
      </div>
    </>
  );
}
